import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UiLanguage } from '@/services/ws-companies/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Button, DropdownList, Icons } from '@/js/libs/ui/src/V2';

import { useUiLanguageLabel } from 'src/hooks/useUiLanguageLabel.ts';
import { changeUiLanguage, getCurrentUiLanguage, toOrderedUiLanguages } from 'src/utils/uiLanguage.ts';

type LanguageDropdownProps = {
  languages?: string[];
};

export const LanguageDropdown = memo(function LanguageDropdown({ languages }: LanguageDropdownProps) {
  const [t, i18n] = useTranslation(['common']);
  const uiLanguageLabel = useUiLanguageLabel();

  const uiLanguages = useMemo(() => (languages ? toOrderedUiLanguages(languages) : Object.values(UiLanguage)), [languages]);
  const currentUiLanguage = getCurrentUiLanguage(i18n.language);

  const onChangeLanguage = useCallbackRef((uiLanguage: UiLanguage) => changeUiLanguage(i18n, uiLanguage));

  return (
    <DropdownList
      minWidth={false}
      dataTestId='language-button'
      renderButton={() => (
        <Button iconOnly ghost title={t('common:LANGUAGE')}>
          <Icons.Globe size={20} />
        </Button>
      )}>
      {uiLanguages.map((uiLanguage) => (
        <DropdownList.DropdownItem
          key={uiLanguage}
          onClick={() => onChangeLanguage(uiLanguage)}
          dataTestId={`language-list-item-${uiLanguage.toLowerCase().replace('_', '-')}`}
          isSelected={uiLanguage === currentUiLanguage}>
          {uiLanguageLabel(uiLanguage)}
        </DropdownList.DropdownItem>
      ))}
    </DropdownList>
  );
});
